/**
 * CommandPalette — global ⌘K command palette (Phase 4.5 + 12.3).
 *
 * `cmdk`-backed dialog wrapped in the radical chrome (sharp corners, hairline
 * border, paper bg, framer-motion scrim with backdrop blur — Phase 12.2
 * motion vocabulary). Renders three classes of entries side-by-side:
 *
 * 1. **Navigace / Vytvořit / Management** — static routes registry from
 *    [palette-routes.ts](../lib/palette-routes.ts); the typeable address
 *    book for power users. Filtered with a folded substring match so Czech
 *    diacritics aren't required.
 * 2. **Recent picks** — last 6 records the user opened (across kontakty +
 *    zákazníci + vozidla); recents only show when the search box is empty.
 * 3. **Live record search** — parallel API queries across Kontakty +
 *    Zákazníci + Vozidla + Nabídky + Smlouvy, debounced 200 ms. Offers and
 *    contracts join the search by their vehicle's číslo zakázky (order
 *    number); vehicles also match it via the generic vehicle search.
 *
 * Supersedes the Phase 1 `<Kbd>⌘K</Kbd>` hint stubs (they now advertise a
 * real binding).
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Icon } from '@/components/Icon';
import { Kbd } from '@/components/Kbd';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import {
  PALETTE_ROUTES,
  filterPaletteRoutes,
  groupPaletteRoutes,
  type PaletteRoute,
} from '@/lib/palette-routes';
import {
  pushRecent,
  readRecents,
  useCommandPalette,
  type PaletteResultType,
  type RecentPick,
} from '@/lib/useCommandPalette';
import { pathModule, useModuleAccess } from '@/lib/permissions';
import {
  contactDisplayName,
  contactInitials,
  vehicleBrandLabel as kontaktBrandLabel,
  vehicleModelLabel as kontaktModelLabel,
} from '@/modules/kontakty/helpers';
import { useContactsQuery } from '@/modules/kontakty/api';
import { useCustomersQuery } from '@/modules/zakaznici/api';
import {
  useNabidkyQuery,
  useSmluvyQuery,
  useVehiclesQuery,
} from '@/modules/vozidla/api';
import { vehicleDisplayName } from '@/modules/vozidla/helpers';

interface PaletteItem {
  type: PaletteResultType;
  id: number;
  label: string;
  sub: string | null;
  initials: string;
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState<RecentPick[]>([]);

  const debouncedQuery = useDebouncedValue(query, 200);

  // Module gating (Phase 11.10 follow-up) — never offer a destination the user
  // can't reach. `gate` mirrors AppRail: show everything until membership
  // resolves, then restrict. Also disables the API searches for modules the
  // user lacks (so a servis-read-only user can't surface contacts/customers/
  // vehicles via ⌘K).
  const { canAccess, can, resolved } = useModuleAccess();
  const gate = (id: Parameters<typeof canAccess>[0]) => !resolved || canAccess(id);
  const canKontakty = gate('kontakty');
  const canZakaznici = gate('zakaznici');
  const canVozidla = gate('vozidla');

  const enabled = debouncedQuery.trim().length >= 2;

  // Routes filter on every keystroke (not debounced — substring match is
  // cheap and the user expects instant feedback for navigation). Access-filter
  // first so forbidden modules never appear. Entries with a per-route `gate`
  // (module admin tools) additionally require that level once resolved.
  const accessibleRoutes = useMemo(
    () =>
      PALETTE_ROUTES.filter((r) => {
        if (r.gate && resolved && !can(r.gate.modul, r.gate.need)) return false;
        const mod = pathModule(r.to);
        return mod ? gate(mod) : true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolved, canAccess, can],
  );
  const filteredRoutes = useMemo(
    () => filterPaletteRoutes(accessibleRoutes, query),
    [accessibleRoutes, query],
  );
  const routeGroups = useMemo(() => groupPaletteRoutes(filteredRoutes), [
    filteredRoutes,
  ]);

  const contactsQuery = useContactsQuery({
    type: 'vsechny',
    name: enabled && canKontakty ? debouncedQuery : '',
    limit: '10',
  });
  const customersQuery = useCustomersQuery({
    search: enabled && canZakaznici ? debouncedQuery : '',
    limit: '10',
  });
  const vehiclesQuery = useVehiclesQuery({
    search: enabled && canVozidla ? debouncedQuery : '',
    limit: '10',
  });
  // Offers + contracts join the search by their vehicle's číslo zakázky only —
  // typing an order number surfaces the matching nabídka / smlouva. (Name and
  // offer/contract-number search stay on their own list pages.)
  const nabidkyQuery = useNabidkyQuery({
    cislo_zakazky: enabled && canVozidla ? debouncedQuery : '',
    limit: '10',
  });
  const smluvyQuery = useSmluvyQuery({
    cislo_zakazky: enabled && canVozidla ? debouncedQuery : '',
    limit: '10',
  });

  // Refresh recent picks every time the palette opens.
  useEffect(() => {
    if (open) setRecents(readRecents());
    if (!open) setQuery('');
  }, [open]);

  const kontaktItems: PaletteItem[] = useMemo(() => {
    if (!enabled) return [];
    return (contactsQuery.data?.contacts ?? []).map((c) => {
      const brand = kontaktBrandLabel(c);
      const model = kontaktModelLabel(c);
      const vehicle = [brand, model].filter(Boolean).join(' ').trim() || null;
      const sub = [
        c.spolecnost && c.jmeno_klienta ? c.spolecnost : null,
        vehicle,
        c.status || null,
      ]
        .filter(Boolean)
        .join(' · ') || null;
      return {
        type: 'kontakt' as const,
        id: c.id_kontaktu,
        label: contactDisplayName(c),
        sub,
        initials: contactInitials(c),
      };
    });
  }, [enabled, contactsQuery.data]);

  const zakaznikItems: PaletteItem[] = useMemo(() => {
    if (!enabled) return [];
    return (customersQuery.data?.customers ?? []).map((c) => {
      const sub = [
        c.ico ? `IČ ${c.ico}` : null,
        c.mesto || null,
        c.email || null,
      ]
        .filter(Boolean)
        .join(' · ') || null;
      return {
        type: 'zakaznik' as const,
        id: c.id_zakaznika,
        label: c.jmeno || `№ ${c.id_zakaznika}`,
        sub,
        initials: initialsFrom(c.jmeno),
      };
    });
  }, [enabled, customersQuery.data]);

  const vozidloItems: PaletteItem[] = useMemo(() => {
    if (!enabled) return [];
    return (vehiclesQuery.data?.vehicles ?? []).map((v) => {
      const sub = [
        v.spz_vozu || null,
        v.vc_auta || null,
        v.stav_vozidla || null,
        v.vin ? `VIN ${v.vin}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || null;
      const display = vehicleDisplayName(v) || `Vozidlo № ${v.id_vozidla}`;
      return {
        type: 'vozidlo' as const,
        id: v.id_vozidla,
        label: display,
        sub,
        initials: initialsFrom(display),
      };
    });
  }, [enabled, vehiclesQuery.data]);

  const nabidkaItems: PaletteItem[] = useMemo(() => {
    if (!enabled) return [];
    return (nabidkyQuery.data?.nabidky ?? [])
      .filter((n) => n.id_nabidky != null)
      .map((n) => {
        const vehicle =
          [n.nazev_znacky, n.nazev_modelu].filter(Boolean).join(' ').trim() || null;
        const label = n.cislo_zakazky
          ? `Zakázka ${n.cislo_zakazky}`
          : `Nabídka № ${n.id_nabidky}`;
        const sub = [vehicle, n.jmeno || null].filter(Boolean).join(' · ') || null;
        return {
          type: 'nabidka' as const,
          id: n.id_nabidky as number,
          label,
          sub,
          initials: 'NB',
        };
      });
  }, [enabled, nabidkyQuery.data]);

  const smlouvaItems: PaletteItem[] = useMemo(() => {
    if (!enabled) return [];
    return (smluvyQuery.data?.smlouvy ?? [])
      .filter((s) => s.id_smlouvy != null)
      .map((s) => {
        const vehicle =
          [s.nazev_znacky, s.nazev_modelu].filter(Boolean).join(' ').trim() || null;
        const label = s.cislo_zakazky
          ? `Zakázka ${s.cislo_zakazky}`
          : `Smlouva № ${s.id_smlouvy}`;
        const sub =
          [vehicle, s.jmeno || null, s.stav_smlouvy || null]
            .filter(Boolean)
            .join(' · ') || null;
        return {
          type: 'smlouva' as const,
          id: s.id_smlouvy as number,
          label,
          sub,
          initials: 'SM',
        };
      });
  }, [enabled, smluvyQuery.data]);

  const totalResults =
    kontaktItems.length +
    zakaznikItems.length +
    vozidloItems.length +
    nabidkaItems.length +
    smlouvaItems.length;
  const noneLoading =
    !contactsQuery.isLoading &&
    !customersQuery.isLoading &&
    !vehiclesQuery.isLoading &&
    !nabidkyQuery.isLoading &&
    !smluvyQuery.isLoading;

  function pick(item: PaletteItem) {
    pushRecent({ type: item.type, id: item.id, label: item.label, sub: item.sub });
    setOpen(false);
    navigateTo(item.type, item.id);
  }

  function pickRecent(r: RecentPick) {
    setOpen(false);
    navigateTo(r.type, r.id);
  }

  function pickRoute(r: PaletteRoute) {
    setOpen(false);
    // PaletteRoute.to is a plain pathname (with optional `?…` query) — sidestep
    // TanStack's typed `to` constraint by using `href`.
    navigate({ href: r.to });
  }

  function navigateTo(type: PaletteResultType, id: number) {
    if (type === 'kontakt') {
      navigate({ to: '/kontakty/$id', params: { id: String(id) } });
    } else if (type === 'zakaznik') {
      navigate({ to: '/zakaznici/$id', params: { id: String(id) } });
    } else if (type === 'nabidka') {
      navigate({ to: '/vozidla/nabidky/$id', params: { id: String(id) } });
    } else if (type === 'smlouva') {
      navigate({ to: '/vozidla/smlouvy/$id', params: { id: String(id) } });
    } else {
      navigate({ to: '/vozidla/$id', params: { id: String(id) } });
    }
  }

  // Phase 12.3: scrim + content driven by framer-motion (Phase 12.2 motion
  // vocabulary — same curve + duration as the Modal scrim). `forceMount` is
  // required so AnimatePresence observes Radix's unmount lifecycle.
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <AnimatePresence>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-[rgba(14,16,20,0.55)] backdrop-blur-md dark:bg-[rgba(0,0,0,0.65)]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: reduce ? 0 : 0.18,
                  ease: [0.2, 0, 0.13, 1],
                }}
              />
            </Dialog.Overlay>
            <Dialog.Content
              asChild
              forceMount
              aria-label="Hledat"
              data-testid="command-palette"
            >
              <motion.div
                className="fixed left-1/2 top-[18vh] z-50 flex max-h-[64vh] w-[calc(100vw-80px)] max-w-[640px] flex-col border border-line bg-paper shadow-[0_24px_64px_rgba(0,0,0,0.30),0_1px_0_rgba(0,0,0,0.05)] focus:outline-none"
                initial={
                  reduce
                    ? { x: '-50%', opacity: 0 }
                    : { x: '-50%', y: -8, opacity: 0, scale: 0.98 }
                }
                animate={{ x: '-50%', y: 0, opacity: 1, scale: 1 }}
                exit={
                  reduce
                    ? { x: '-50%', opacity: 0 }
                    : { x: '-50%', y: -4, opacity: 0, scale: 0.98 }
                }
                transition={{
                  duration: reduce ? 0 : 0.2,
                  ease: [0.2, 0, 0.13, 1],
                }}
              >
                <Dialog.Title className="sr-only">Vyhledat</Dialog.Title>
                <Dialog.Description className="sr-only">
                  Hledá v navigaci, kontaktech, zákaznících, vozidlech, nabídkách
                  a smlouvách (i podle čísla zakázky). Použij ⌘K kdekoli v
                  aplikaci.
                </Dialog.Description>
                <Command label="Vyhledat" shouldFilter={false}>
                  <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
                    <Icon name="search" size={14} className="text-ink-3" />
                    <Command.Input
                      value={query}
                      onValueChange={setQuery}
                      placeholder="Skoč kamkoli — nebo hledej kontakt, zákazníka, vozidlo…"
                      className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-ink outline-none placeholder:italic placeholder:text-ink-3/55"
                      data-testid="command-palette-input"
                    />
                    <Kbd>⌘K</Kbd>
                  </div>
                  <Command.List className="max-h-[52vh] overflow-y-auto">
                    {!enabled && recents.length > 0 && (
                      <Command.Group
                        heading="Naposledy"
                        className="px-2 py-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-ink-3"
                      >
                        {recents.map((r) => (
                          <Command.Item
                            key={`recent-${r.type}-${r.id}`}
                            value={`recent-${r.type}-${r.id}-${r.label}`}
                            onSelect={() => pickRecent(r)}
                            className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-[13px] text-ink-2 data-[selected=true]:bg-surface-2"
                          >
                            <ResultIcon type={r.type} />
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate font-semibold text-ink">{r.label}</span>
                              <span className="truncate text-[11px] text-ink-3">
                                {[TYPE_LABEL[r.type], r.sub].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    )}

                    {/* Routes — always shown, filtered by the query. Section
                        order intentionally puts recents above navigation so a
                        user with recents lands on a relevant record first;
                        empty-input + no-recents lands on Navigace by default. */}
                    {routeGroups.map((g) => (
                      <RouteGroup
                        key={g.group}
                        heading={g.group}
                        routes={g.routes}
                        onPick={pickRoute}
                      />
                    ))}

                    {!enabled && recents.length === 0 && routeGroups.length === 0 && (
                      <div className="px-4 py-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                        Začni psát · alespoň 2 znaky
                      </div>
                    )}

                    {enabled && kontaktItems.length > 0 && (
                      <ResultGroup heading="Kontakty" items={kontaktItems} onPick={pick} />
                    )}
                    {enabled && zakaznikItems.length > 0 && (
                      <ResultGroup heading="Zákazníci" items={zakaznikItems} onPick={pick} />
                    )}
                    {enabled && vozidloItems.length > 0 && (
                      <ResultGroup heading="Vozidla" items={vozidloItems} onPick={pick} />
                    )}
                    {enabled && nabidkaItems.length > 0 && (
                      <ResultGroup heading="Nabídky" items={nabidkaItems} onPick={pick} />
                    )}
                    {enabled && smlouvaItems.length > 0 && (
                      <ResultGroup heading="Smlouvy" items={smlouvaItems} onPick={pick} />
                    )}

                    {enabled &&
                      noneLoading &&
                      totalResults === 0 &&
                      routeGroups.length === 0 && (
                        <Command.Empty className="px-4 py-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                          Žádné výsledky · zkuste jiné slovo
                        </Command.Empty>
                      )}
                  </Command.List>
                  <div className="border-t border-line px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    <span>↑↓ — navigace · ⏎ — otevřít · Esc — zavřít</span>
                  </div>
                </Command>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}

const TYPE_LABEL: Record<PaletteResultType, string> = {
  kontakt: 'Kontakt',
  zakaznik: 'Zákazník',
  vozidlo: 'Vozidlo',
  nabidka: 'Nabídka',
  smlouva: 'Smlouva',
};

function ResultGroup({
  heading,
  items,
  onPick,
}: {
  heading: string;
  items: PaletteItem[];
  onPick: (item: PaletteItem) => void;
}) {
  return (
    <Command.Group
      heading={heading}
      className="px-2 py-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-ink-3"
    >
      {items.map((item) => (
        <Command.Item
          key={`${item.type}-${item.id}`}
          value={`${item.type}-${item.id}-${item.label}`}
          onSelect={() => onPick(item)}
          className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-[13px] text-ink-2 data-[selected=true]:bg-surface-2"
        >
          <span
            aria-hidden
            className="grid size-7 shrink-0 place-items-center border border-line-2 font-mono text-[10px] font-medium uppercase tracking-[-0.02em] text-ink-2"
          >
            {item.initials}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-semibold text-ink">{item.label}</span>
            {item.sub && (
              <span className="truncate text-[11px] text-ink-3">{item.sub}</span>
            )}
          </span>
        </Command.Item>
      ))}
    </Command.Group>
  );
}

function ResultIcon({ type }: { type: PaletteResultType }) {
  const glyph =
    type === 'vozidlo'
      ? '🚗'
      : type === 'zakaznik'
        ? '⚲'
        : type === 'nabidka'
          ? '₸'
          : type === 'smlouva'
            ? '§'
            : '↺';
  return (
    <span
      aria-hidden
      className="grid size-7 shrink-0 place-items-center border border-line-2 font-mono text-[11px] font-medium text-ink-3"
    >
      {glyph}
    </span>
  );
}

function RouteGroup({
  heading,
  routes,
  onPick,
}: {
  heading: string;
  routes: readonly PaletteRoute[];
  onPick: (r: PaletteRoute) => void;
}) {
  return (
    <Command.Group
      heading={heading}
      className="px-2 py-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-ink-3"
    >
      {routes.map((r) => (
        <Command.Item
          key={r.id}
          value={r.id}
          onSelect={() => onPick(r)}
          data-testid={`palette-route-${r.id}`}
          className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-[13px] text-ink-2 data-[selected=true]:bg-surface-2"
        >
          <span
            aria-hidden
            className="grid size-7 shrink-0 place-items-center border border-line-2 font-mono text-[10.5px] font-medium text-ink-3"
          >
            ↗
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-semibold text-ink">{r.label}</span>
            {r.sub && (
              <span className="truncate text-[11px] text-ink-3">{r.sub}</span>
            )}
          </span>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            {r.to.replace('/', '/')}
          </span>
        </Command.Item>
      ))}
    </Command.Group>
  );
}

function initialsFrom(source: string | null | undefined): string {
  const s = (source ?? '').trim();
  if (!s) return '·';
  const parts = s.split(/\s+/).filter((p) => /^[A-Za-zÀ-ž]/.test(p));
  if (parts.length === 0) return s.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
